import { FC, useEffect, useState } from 'react'
import Auth from '@aws-amplify/auth'

import { PhoneNumberInput } from 'featureSliced/entities/Input/PhoneNumberInput'
import { Spinner } from 'featureSliced/entities/Spinner'
import { Input } from 'featureSliced/entities/Input'
import { RenderIf } from 'components/RenderIf'
import { TextArea } from 'components/TextArea'

import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'

import { TermsAndConditionsCheckbox } from 'featureSliced/features/TermsConditions/checkBox'
import { TimeLocation } from 'featureSliced/widgets/TimeLocationNew'

import { Footer } from './Footer'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { PaymentSteps, ResultParams, ResultType } from './types'

import { TableDiv } from 'components/Cart.tsx/styled'
import {
  CredentialsContainer,
  CredentialsContainerInnerFirst,
  CredentialsContainerInnerSecond,
  IdDescription,
  OrderCommentLabel,
  SpinnerContainer,
  TermsAndConditionsContainer,
} from './styled'
import { clickCollectPOST } from 'featureSliced/shared/api/clickCollect/POST'
import { DataTrans } from 'featureSliced/features/Datatrans/DataTrans'
import { clickCollectGET } from 'featureSliced/shared/api/clickCollect/GET'
import { errorHandler } from 'components/ErrorHandler'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { CC_CardsModal } from './CC_CardsModal'

const ORDER_COMMENT_MAX_LENGTH = 500

type CredentialsProps = {
  moveToResult: (result: ResultType, orderNumberToSet?: number) => void
}

export const Credentials: FC<CredentialsProps> = ({ moveToResult }) => {
  // Hooks
  const t = useTranslations()
  const openModal = useOpenModal()
  const { getProductPrice, getAllProductsPrice } = useGetProductPrice()

  // Store
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const cartProducts = useMySelector((state) => state.bucket.products)
  const productList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const { paymentProviderType, paymentOnSite } = useMySelector(
    (state) => state.app.paymentConfig
  )

  // State
  const [trxId, setTrxId] = useState<string>()
  const [name, setName] = useState('')
  const [isInit, setIsInit] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [orderComment, setOrderComment] = useState('')
  const [isTermsChecked, setIsTermsChecked] = useState(false)
  const [isPayButtonEnabled, setIsPayButtonEnabled] = useState<boolean>(false)
  const [isOrderCreating, setIsOrderCreating] = useState<boolean>(false)
  const [loyaltyCardBalance, setLoyaltyCardBalance] = useState<number>(0)

  // useEffects
  useEffect(() => {
    initUserData()
    checkIfLoyaltyCardExists()
  }, [isUserAuth])

  useEffect(() => {
    if (isInit && !isUserAuth) {
      setIsPayButtonEnabled(
        !!name.length && phoneNumber.length > 6 && isTermsChecked
      )
    } else if (isInit && isUserAuth) {
      setIsPayButtonEnabled(!!name.length && phoneNumber.length > 6)
    }
  }, [name, phoneNumber, isInit, isTermsChecked])

  // Functions
  const initUserData = async () => {
    if (isUserAuth) {
      const currentUserInfo = await Auth.currentUserInfo()
      const attributes = currentUserInfo?.attributes
      if (attributes) {
        setName(attributes.name)
        setPhoneNumber(attributes.phone_number)
      }
    }
    if (!isUserAuth) {
      const selfServiceName = handleLocalStorage('selfServiceName')
      const selfServicePhone = handleLocalStorage('selfServicePhone')
      if (selfServiceName?.length) {
        setName(selfServiceName)
      }
      if (selfServicePhone?.length) {
        setPhoneNumber(selfServicePhone)
      }
    }
    const storedComment = handleLocalStorage('orderComment')
    if (typeof storedComment === 'string' && storedComment.length) {
      setOrderComment(storedComment)
    }
    setIsInit(true)
  }

  const checkIfLoyaltyCardExists = async () => {
    try {
      if (isUserAuth) {
        const res = await clickCollectGET.customerCard()
        const total = Number(res?.total)
        setLoyaltyCardBalance(!isNaN(total) ? total : 0)
      }
    } catch (e) {
      errorHandler(e)
    }
  }

  const handleCommentChange = (value: string) => {
    const next = value.slice(0, ORDER_COMMENT_MAX_LENGTH)
    setOrderComment(next)
    handleLocalStorage({ orderComment: next })
  }

  const startPayment = async () => {
    try {
      setIsOrderCreating(true)
      if (!isUserAuth || paymentProviderType === 0 || paymentOnSite) {
        // no user auth ==> no need to select card
        // paymentProviderType == 0 ==> payment on site, no card selection needed
        handlePayment()
      } else if (isUserAuth) {
        const currentUserInfo = await Auth.currentUserInfo()
        const attributes = currentUserInfo?.attributes
        if (
          (!attributes || !attributes?.['custom:active_card_token']) &&
          !loyaltyCardBalance
        ) {
          // no active card and no loyalty card balance ==> no need to select card
          handlePayment()
        } else {
          // active card and/or loyalty card balance CASE
          // open created modal here
          const tipsValue = 0
          openModal({
            id: 'PAYMENT_CARDS_MODAL',
            title: t('order.choosePaymentVariant'),
            components: [
              ({ closeThisModal }) => (
                <CC_CardsModal
                  handlePayment={handlePayment}
                  closeModal={closeThisModal}
                  cardsData={{
                    isLoyaltyCardExisting: !!loyaltyCardBalance,
                    isLoyaltyCardDisabled:
                      loyaltyCardBalance <
                      getAllProductsPrice(getProductsToPay()) + tipsValue,
                    userCardData: attributes?.['custom:active_card_token'],
                  }}
                />
              ),
            ],
          })
        }
      }
    } catch (e) {
      moveToResult('fail')
      setIsOrderCreating(false)
    }
  }

  const handlePayment = async (
    useNewCard = false,
    updateActiveCard = false,
    useLoyaltyCard = false
  ) => {
    try {
      setIsOrderCreating(true)
      const productsToPay = getProductsToPay()
      const result = await clickCollectPOST.createOrder(
        productsToPay,
        useNewCard,
        updateActiveCard,
        useLoyaltyCard,
        name,
        phoneNumber,
        orderComment
      )
      const { orderNr, paymentProvider, transactionId } = result
      if (paymentProvider === 1 && transactionId) {
        setTrxId(transactionId)
      } else if (orderNr) {
        moveToResult('success', orderNr)
        setIsOrderCreating(false)
      } else {
        moveToResult('fail')
        setIsOrderCreating(false)
      }
    } catch (e) {
      moveToResult('fail')
      setIsOrderCreating(false)
    }
  }

  const getProductsToPay = () => {
    const productsNotes = handleLocalStorage('products_notes')
    const productsToPay = cartProducts.reduce(
      (acc: any[], curr: IBucketProduct) => {
        let foundProduct: IProduct | undefined = productList?.find(
          (sp) => sp.id === curr.productId
        )
        if (!foundProduct) {
          foundProduct = productList
            ?.reduce((acc: IProduct[], current) => {
              if (current.innerCategoryItems) {
                return [...acc, ...current.innerCategoryItems]
              }
              return acc
            }, [])
            ?.find((st) => st.id === curr.productId)
        }
        return [
          ...acc,
          {
            ...foundProduct,
            ...curr,
            count: curr?.count || 0,
            kitchenInfo: curr?.kitchenInfo || '{}',
            price: getProductPrice(
              foundProduct as IProduct,
              foundProduct?.categoryId || 0
            ).finalPrice,
            productId: curr?.productId,
            customerNote:
              productsNotes?.[
                `${foundProduct?.categoryId || 0}_${foundProduct?.id || 0}`
              ],
          },
        ]
      },
      []
    )
    return productsToPay
  }

  return (
    <>
      <RenderIf condition={typeof trxId === 'string' && !!trxId?.length}>
        <DataTrans transactionId={trxId as string} />
      </RenderIf>

      <RenderIf condition={isUserAuth && !isInit}>
        <SpinnerContainer>
          <Spinner type="transparent" />
        </SpinnerContainer>
      </RenderIf>

      <TimeLocation />
      <CredentialsContainer>
        <CredentialsContainerInnerFirst>
          <TableDiv>{t('order.yourOrder')}</TableDiv>
          <IdDescription>
            {t('clickCollect.identificationDescription')}
          </IdDescription>
          <Input
            value={name}
            placeholder={t('clickCollect.nameEnter')}
            onChange={(e: any) => setName(e.currentTarget.value)}
            // onFocus={handleInputFocus}
            // onBlur={handleInputBlur}
          />
          <PhoneNumberInput
            value={phoneNumber}
            onChange={(newValue) => setPhoneNumber(newValue)}
            // onFocus={handleInputFocus}
            // onBlur={handleInputBlur}
          />
          <OrderCommentLabel>
            {t('clickCollect.orderCommentLabel')}
          </OrderCommentLabel>
          <TextArea
            value={orderComment}
            rows="3"
            maxLength={ORDER_COMMENT_MAX_LENGTH}
            placeholder={t('clickCollect.orderCommentPlaceholder')}
            onChange={(e: any) => handleCommentChange(e.currentTarget.value)}
          />
        </CredentialsContainerInnerFirst>

        <CredentialsContainerInnerSecond>
          <RenderIf condition={!isUserAuth}>
            <TermsAndConditionsContainer>
              <TermsAndConditionsCheckbox
                isChecked={isTermsChecked}
                handleCheck={setIsTermsChecked}
              />
            </TermsAndConditionsContainer>
          </RenderIf>

          <Footer
            step={PaymentSteps.CREDENTIALS}
            onConfirmClick={startPayment}
            isButtonDisabled={!isPayButtonEnabled}
            isLoading={isOrderCreating}
          />
        </CredentialsContainerInnerSecond>
      </CredentialsContainer>
    </>
  )
}
